import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTutorialStore } from './useTutorialStore';
import { getStepsForTutorial } from './tutorialSteps';

/**
 * Step id -> selector for the element to draw a blue box around (all tutorials).
 * Used for icon/button steps only. Steps with combined highlight (icon + menu) get their
 * blue box from TutorialCombinedHighlight.
 */
const BLUE_BOX_SELECTORS: Record<string, string> = {
  // Filters: sub-elements inside date picker
  'date-range-mode-toggle': '[data-tutorial="date-range-quick-select-button"]',
  'date-range-custom-range': '[data-tutorial="date-range-custom-range-button"]',
  // Views & Cards
  'card-info-icon': '[data-tutorial="card-info-icon"]',
  'create-new-tab': '[data-tutorial="create-new-tab"]',
  // AI Assistant
  'ai-send': '[data-tutorial="ai-send"]',
  // Node Graph Overview (step 1 — Create Node Graph in demo modal)
  'node-graph-modal-create': '[data-tutorial="product-modal-create-node-graph"]',
  // Node Graph: step 9 — vis node + label (same frame div as step 10 trigger)
  'node-graph-context-search': '[data-tutorial="network-chart-tutorial-context-node-frame"]',
  'ai-panel-toggle': '[data-tutorial="ai-panel-toggle"]',
  // Assets
  'assets-filters-tab': '[data-tutorial="assets-filters-tab"]',
  'assets-views-tab': '[data-tutorial="assets-views-tab"]',
  'assets-node-graphs-tab': '[data-tutorial="assets-node-graphs-tab"]',
  'assets-reports-tab': '[data-tutorial="assets-reports-tab"]',
  // Account menu step: blue box around the Account link in the dropdown (icon box is from TutorialCombinedHighlight)
  'account-menu': '[data-tutorial="account-menu-link"]',
  // API
  'api-tab': '[data-tutorial="api-tab"]',
  'api-languages': '[data-tutorial="api-languages"]',
  // Account & Settings
  'theme-preference': '[data-tutorial="theme-preference"]',
  'manage-subscription': '[data-tutorial="manage-subscription"]',
};

/**
 * Draws a blue box (2px solid #2563eb) around the target element for any tutorial step
 * that highlights a small icon/button/tab. Tutorial-agnostic; when the current step id
 * is in BLUE_BOX_SELECTORS, the box is rendered.
 */
export const TutorialFiltersBlueBox: React.FC = () => {
  const { isRunning, currentTutorialId, stepIndex } = useTutorialStore();
  const steps = currentTutorialId ? getStepsForTutorial(currentTutorialId) : [];
  const currentStepId = steps[stepIndex]?.id;
  const selector = currentStepId ? BLUE_BOX_SELECTORS[currentStepId] : null;

  const [rect, setRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    if (!isRunning || !selector) {
      setRect(null);
      return;
    }

    const update = () => {
      const el = document.querySelector(selector);
      setRect(el ? el.getBoundingClientRect() : null);
    };

    update();
    const raf = requestAnimationFrame(update);
    const t = setTimeout(update, 100);
    const t2 =
      currentStepId === 'node-graph-modal-create' ? setTimeout(update, 280) : undefined;
    const t3 =
      currentStepId === 'node-graph-modal-create' ? setTimeout(update, 520) : undefined;
    const t4 =
      currentStepId === 'node-graph-context-search' ? setTimeout(update, 350) : undefined;
    const t5 =
      currentStepId === 'node-graph-context-search' ? setTimeout(update, 700) : undefined;
    const resize = () => update();
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', resize, true);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      if (t2 !== undefined) clearTimeout(t2);
      if (t3 !== undefined) clearTimeout(t3);
      if (t4 !== undefined) clearTimeout(t4);
      if (t5 !== undefined) clearTimeout(t5);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', resize, true);
    };
  }, [isRunning, currentTutorialId, currentStepId, selector]);

  if (!rect || typeof document === 'undefined') return null;

  // Node graph step 1 highlights a control inside the product modal (z-index above driver)
  const zIndex = currentStepId === 'node-graph-modal-create' ? 10052 : 9999;

  const el = (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        pointerEvents: 'none',
        zIndex,
        border: '2px solid #2563eb',
        borderRadius: 4,
        boxSizing: 'border-box',
      }}
    />
  );

  return createPortal(el, document.body);
};
